import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { Order, Driver, SystemConfig, OperatingContext, SmartSplitSuggestion } from '../types';
import { decodeBase64, decodeAudioData, formatLogisticContext } from '../services/sofiaUtils';
import { Truck, Volume2 } from 'lucide-react';
import { DistanceService } from '../services/parserService';
import React from 'react';

const HISTORY_STORAGE_KEY = 'sal_sofia_history_v1';

const PROFESSIONAL_KEYWORDS = [
    'pedido', 'rota', 'entregador', 'motoboy', 'tempo', 'atraso', 'chuva', 'transito',
    'custo', 'app', 'ifood', 'bairro', 'status', 'ajuda', 'analise', 'distribuição',
    'estratégia', 'caixa', 'sangria', 'mapa', 'recon', 'balcão', 'retirada', 'faço'
];

export const useSofia = (
    orders: Order[],
    drivers: Driver[],
    config: SystemConfig,
    context: OperatingContext,
    actions: any,
    logisticAnalysis?: SmartSplitSuggestion | null
) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [lastResponse, setLastResponse] = useState('Protocolo Sofia online.');
    const [conversationHistory, setConversationHistory] = useState<{ type: 'user' | 'sofia', text: string, timestamp: number }[]>([]);
    const [isProactiveSpeaking, setIsProactiveSpeaking] = useState(false);

    const proactiveOutputAudioContextRef = useRef<AudioContext | null>(null);
    const proactiveNextStartTimeRef = useRef<number>(0);
    const proactiveAudioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
    const audioQueueRef = useRef<string[]>([]);
    const isProcessingQueueRef = useRef(false);
    const queueTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isGeminiTTSQuotaExceeded = useRef(false);
    const synthRef = useRef<SpeechSynthesis | null>(null);

    const latestStateRef = useRef({ orders, drivers, config, context, actions, logisticAnalysis });
    const prevBoomerangActiveRef = useRef<boolean>(false);
    const prevHeldOrderIdsRef = useRef<Set<string>>(new Set());
    const lastExternalFollowUpRef = useRef<Record<string, number>>({});

    useEffect(() => {
        latestStateRef.current = { orders, drivers, config, context, actions, logisticAnalysis };
    }, [orders, drivers, config, context, actions, logisticAnalysis]);

    useEffect(() => {
        const saved = localStorage.getItem(HISTORY_STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const fresh = parsed.filter((m: any) => m.timestamp > (Date.now() - 86400000));
                setConversationHistory(fresh);
            } catch (e) { }
        }
        synthRef.current = window.speechSynthesis;
    }, []);

    useEffect(() => {
        if (conversationHistory.length > 0) {
            localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(conversationHistory));
        }
    }, [conversationHistory]);

    // --- REST API CALL HELPER ---
    const callGeminiRest = async (apiKey: string, contents: any, systemInstruction?: string, generationConfig: any = {}) => {
        const model = "gemini-2.0-flash-exp";
        const body: any = { contents, generationConfig };
        if (systemInstruction) {
            body.system_instruction = { parts: [{ text: systemInstruction }] };
        }

        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error?.message || "Erro na API Gemini");
        }
        return await response.json();
    };

    // --- AUDIO SYSTEM ---
    const processQueue = async () => {
        if (isProcessingQueueRef.current || audioQueueRef.current.length === 0) return;

        isProcessingQueueRef.current = true;
        setIsProactiveSpeaking(true);
        const text = audioQueueRef.current.shift()!;

        const vConfig = config.communication?.sofiaVoiceConfig;
        const useGeminiTTS = vConfig?.proactiveQuality === 'gemini-tts' && !isGeminiTTSQuotaExceeded.current;
        const apiKey = config.geminiApiKey;
        const voiceName = vConfig?.ttsVoiceName || 'Kore';

        const finishItem = () => {
            queueTimeoutRef.current = setTimeout(() => {
                isProcessingQueueRef.current = false;
                if (audioQueueRef.current.length > 0) processQueue();
                else setIsProactiveSpeaking(false);
            }, 2000);
        };

        if (useGeminiTTS && apiKey) {
            try {
                const data = await callGeminiRest(apiKey, [{ role: 'user', parts: [{ text }] }], undefined, {
                    responseModalities: ["AUDIO"],
                    speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName } } }
                });

                const base64Audio = data.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
                if (base64Audio) {
                    if (!proactiveOutputAudioContextRef.current) {
                        proactiveOutputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
                    }
                    if (proactiveNextStartTimeRef.current < proactiveOutputAudioContextRef.current.currentTime) {
                        proactiveNextStartTimeRef.current = proactiveOutputAudioContextRef.current.currentTime;
                    }
                    const audioBuffer = await decodeAudioData(decodeBase64(base64Audio), proactiveOutputAudioContextRef.current, 24000, 1);
                    const source = proactiveOutputAudioContextRef.current.createBufferSource();
                    source.buffer = audioBuffer;
                    source.connect(proactiveOutputAudioContextRef.current.destination);
                    source.start(proactiveNextStartTimeRef.current);
                    proactiveNextStartTimeRef.current += audioBuffer.duration;
                    proactiveAudioSourcesRef.current.add(source);
                    source.onended = () => { proactiveAudioSourcesRef.current.delete(source); finishItem(); };
                    return;
                }
            } catch (e: any) {
                console.error("Gemini TTS Error:", e);
                if (e.message?.includes("429")) isGeminiTTSQuotaExceeded.current = true;
            }
        }

        if (synthRef.current) {
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = 'pt-BR'; utterance.rate = 1.2;
            utterance.onend = () => finishItem();
            synthRef.current.speak(utterance);
        } else finishItem();
    };

    const speak = (text: string) => {
        if (!text) return;
        audioQueueRef.current.push(text);
        processQueue();
    };

    const handleProcessCommandText = async (inputText: string) => {
        if (!inputText.trim()) return;
        const activeOrders = latestStateRef.current.orders.filter(o => !['delivered', 'archived', 'canceled'].includes(o.status));

        const lowerInput = inputText.toLowerCase();
        const isOrientationCommand = lowerInput.includes("o que eu faço") || lowerInput.includes("o que eu faco");
        const isProfessional = isOrientationCommand || PROFESSIONAL_KEYWORDS.some(kw => lowerInput.includes(kw));

        if (!isProfessional && lowerInput.length > 5) {
            const policyMsg = "Diretriz Operacional: Interações com a Sofia devem ser estritamente profissionais.";
            setConversationHistory(prev => [...prev.slice(-19), { type: 'user', text: inputText, timestamp: Date.now() }, { type: 'sofia', text: policyMsg, timestamp: Date.now() }]);
            setLastResponse(policyMsg);
            return;
        }

        setIsThinking(true);
        try {
            const apiKey = config.geminiApiKey;
            if (!apiKey) throw new Error("API Key não configurada.");

            const analysis = latestStateRef.current.logisticAnalysis;
            const reasoning = analysis?.waitSuggestion?.message || "Análise padrão.";
            const savings = analysis?.metrics?.cashDelta?.signedDelta?.toFixed(2) || "0.00";
            const detailedContext = `CENÁRIO: Clima ${latestStateRef.current.context.rainMode}, Pedidos Pendentes ${activeOrders.length}. ESTRATÉGIA: ${reasoning}, Econ R$ ${savings}. LOGISTICA: ${formatLogisticContext(latestStateRef.current.logisticAnalysis)}`;

            let userPrompt = inputText;
            if (isOrientationCommand) {
                userPrompt = `COMANDO DE PROTOCOLO: O que eu faço? Responda para um despachante de forma MUITO SIMPLES e direta. Use no máximo 3 ou 4 tópicos curtos. PROIBIDO usar termos técnicos como "ativos disponíveis", "modality", "delta", "contexto" ou linguajar robótico. 

                IMPORTANTE: Verifique o campo "strategy_rationale" no contexto. Se houver Retenção ou Bumerangue ativos, explique-os como decisões inteligentes de economia e eficiência, e não como atrasos. Diga exatamente o que o despachante deve fazer agora.`;
            }

            const data = await callGeminiRest(
                apiKey,
                [{ role: 'user', parts: [{ text: `${detailedContext}\n\nUSUÁRIO: ${userPrompt}` }] }],
                config.sofiaPersonalityPrompt
            );

            let finalResponseText = data.candidates?.[0]?.content?.parts?.[0]?.text || "Sem resposta.";
            setLastResponse(finalResponseText);
            setConversationHistory(prev => [...prev.slice(-19), { type: 'user', text: inputText, timestamp: Date.now() }, { type: 'sofia', text: finalResponseText, timestamp: Date.now() }]);
        } catch (e: any) {
            console.error("Sofia Error:", e);
            setLastResponse(`Falha no link neural: ${e.message}`);
        } finally { setIsThinking(false); }
    };

    const runProactiveScan = () => {
        const { orders, actions } = latestStateRef.current;
        const activePool = orders.filter(o => !['delivered', 'archived', 'canceled'].includes(o.status));
        if (activePool.length === 0) return;

        const externalDispatched = orders.filter(o => o.status === 'dispatched_3rd_party');
        const now = Date.now();

        externalDispatched.forEach(order => {
            if (!order.externalCallConfirmed) {
                const elapsed = (now - (order.statusUpdatedAt || order.createdAt)) / 60000;
                if (elapsed > 5) {
                    const msg = `Pedido #${order.id} (${order.customerName.split(' ')[0]}) marcado como App, mas sem confirmação.`;
                    toast.error(msg, { id: `ext-call-${order.id}` });
                }
            }
        });

        const opportunity = latestStateRef.current.logisticAnalysis?.sequentialOpportunity;
        if (opportunity?.active && !prevBoomerangActiveRef.current) {
            const driver = drivers.find(d => d.id === opportunity.driverId);
            if (driver && driver.lat && driver.lng) {
                const dist = DistanceService.getDistanceSync(config.businessLat, config.businessLng, driver.lat, driver.lng);
                if (dist < 1.5) {
                    speak(`${opportunity.driverName} está voltando ao Hub! Já deixe os pedidos preparados para ele sair novamente.`);
                } else {
                    speak(`Oportunidade de Entrega e Volta com ${opportunity.driverName}.`);
                }
            } else {
                speak(`Oportunidade de Entrega e Volta com ${opportunity.driverName}.`);
            }
        }
        prevBoomerangActiveRef.current = !!opportunity?.active;

        // Trigger para pedidos retidos (Proatividade)
        const heldOrders = latestStateRef.current.logisticAnalysis?.heldOrders || [];
        if (heldOrders.length > 0 && heldOrders.length > prevHeldOrderIdsRef.current.size) {
            const readyCount = heldOrders.filter(o => o.status === 'ready' || o.isReady).length;
            if (readyCount > 0) {
                speak(`Sugestão: Não envie estes ${readyCount} pedidos agora. Aguarde um pouco mais para agrupar e reduzir custos de entrega.`);
            }
        }
        prevHeldOrderIdsRef.current = new Set(heldOrders.map(o => o.id));
    };

    useEffect(() => {
        const interval = setInterval(runProactiveScan, 10000);
        return () => { clearInterval(interval); proactiveOutputAudioContextRef.current?.close(); };
    }, []);

    useEffect(() => {
        const handleJarvisSay = (event: CustomEvent) => {
            const text = event.detail?.text;
            if (text) { setLastResponse(text); speak(text); setConversationHistory(prev => [...prev.slice(-19), { type: 'sofia', text: text, timestamp: Date.now() }]); }
        };
        window.addEventListener('SAL_JARVIS_SAY' as any, handleJarvisSay as any);
        return () => window.removeEventListener('SAL_JARVIS_SAY' as any, handleJarvisSay as any);
    }, []);

    return { isOpen, setIsOpen, isThinking, lastResponse, conversationHistory, handleProcessCommandText, isProactiveSpeaking };
};